import { computed, onActivated, onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';

function normalizeItem(item) {
  return {
    id: item?.id || item?.sm2_id || '',
    sm2_id: item?.sm2_id || item?.id || '',
    public_key: item?.public_key || '',
    private_key: item?.private_key || '',
    created_time: item?.created_time || '',
    created_at_ms: Number(item?.created_at_ms || 0),
    status: item?.status || (item?.valid ? 'valid' : 'invalid'),
    valid: item?.valid !== false && item?.status !== 'invalid',
    invalid_time: item?.invalid_time || '',
    delete_time: item?.delete_time || '',
    redis_key: item?.redis_key || '',
  };
}

export function useMazdaSm2Keys(mazdaPost) {
  const loading = ref(false);
  const adding = ref(false);
  const items = ref([]);
  const stats = ref({ total: 0, valid: 0, invalid: 0 });
  const validLimit = ref(5);
  const invalidRetentionHours = ref(12);
  const redisDb = ref('DB1 / mazda');
  const idsSetKey = ref('api:funcMazda:sm2:keyIds');
  const historySetKey = ref('api:funcMazda:sm2:idHistory');
  const keyPrefix = ref('api:funcMazda:sm2:key:');

  const validItems = computed(() => items.value.filter((item) => item.valid));
  const invalidItems = computed(() => items.value.filter((item) => !item.valid));
  const validCountText = computed(() => `${validItems.value.length}/${validLimit.value}`);

  const applyInfo = (data) => {
    redisDb.value = data.redisDb || 'DB1 / mazda';
    idsSetKey.value = data.idsSetKey || idsSetKey.value;
    historySetKey.value = data.historySetKey || historySetKey.value;
    keyPrefix.value = data.keyPrefix || keyPrefix.value;
    validLimit.value = Number(data.validLimit || 5);
    invalidRetentionHours.value = Number(data.invalidRetentionHours || 12);
    items.value = Array.isArray(data.items) ? data.items.map(normalizeItem) : [];
    stats.value = {
      total: Number(data.stats?.total ?? items.value.length),
      valid: Number(data.stats?.valid ?? validItems.value.length),
      invalid: Number(data.stats?.invalid ?? invalidItems.value.length),
    };
  };

  const loadInfo = async () => {
    if (loading.value) return;

    loading.value = true;
    try {
      const response = await mazdaPost('sm2KeysInfo');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '读取 SM2 密钥失败');
        return;
      }
      applyInfo(data);
    } catch (error) {
      ElMessage.error(`读取 SM2 密钥失败: ${error.response?.data?.message || error.message}`);
    } finally {
      loading.value = false;
    }
  };

  const addKey = async () => {
    if (adding.value) return;

    try {
      await ElMessageBox.confirm(
        `确定新增一组 SM2 密钥吗？\n\n最多 ${validLimit.value} 组保持有效；超过后最早的一组会失效，并在 ${invalidRetentionHours.value} 小时后自动清理。`,
        '新增 SM2 密钥',
        { confirmButtonText: '新增一组', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    adding.value = true;
    try {
      const response = await mazdaPost('sm2KeysAdd', {}, { timeout: 120000 });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '新增 SM2 密钥失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || 'SM2 密钥已新增');
    } catch (error) {
      ElMessage.error(`新增 SM2 密钥失败: ${error.response?.data?.message || error.message}`);
    } finally {
      adding.value = false;
    }
  };

  const copyValue = async (value, label) => {
    const text = String(value || '');
    if (!text) {
      ElMessage.warning(`${label} 为空`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      ElMessage.success(`${label} 已复制`);
    } catch {
      ElMessage.error('复制失败，请手动选中文本复制');
    }
  };

  onMounted(loadInfo);
  onActivated(loadInfo);

  return {
    loading,
    adding,
    items,
    stats,
    validLimit,
    invalidRetentionHours,
    redisDb,
    idsSetKey,
    historySetKey,
    keyPrefix,
    validItems,
    invalidItems,
    validCountText,
    loadInfo,
    addKey,
    copyValue,
  };
}
